<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div
    class="editor d-flex flex-column flex-grow-1"
    :class="{ 'editor--disabled': usePlaceholder }"
  >
    <div>
      <v-alert class="mb-4" id="saveWorkInfoMessage" type="info" v-if="showSaveWorkMessage">
        {{ MESSAGES.INFO.SAVE_WORK }}
      </v-alert>
    </div>
    <p v-if="usePlaceholder !== null" class="mb-0">Value</p>
    <div class="menubar mt-0" v-if="!usePlaceholder">
      <button
        id="content-editor-tip-tap--undo-btn"
        type="button"
        class="menubar__button"
        title="Undo"
        @click="editor.chain().focus().undo().run()"
      >
        <v-icon>mdi-undo</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--redo-btn"
        type="button"
        class="menubar__button"
        title="Redo"
        @click="editor.chain().focus().redo().run()"
      >
        <v-icon>mdi-redo</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--bold-btn"
        type="button"
        class="menubar__button"
        title="Bold"
        :class="{ 'is-active': editor?.isActive('bold') }"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <v-icon>mdi-format-bold</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--italic-btn"
        type="button"
        class="menubar__button"
        title="Italic"
        :class="{ 'is-active': editor?.isActive('italic') }"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <v-icon>mdi-format-italic</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--strikethrough-btn"
        type="button"
        class="menubar__button"
        title="Strikethrough"
        :class="{ 'is-active': editor?.isActive('strike') }"
        @click="editor.chain().focus().toggleStrike().run()"
      >
        <v-icon>mdi-format-strikethrough-variant</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--underline-btn"
        type="button"
        class="menubar__button"
        title="Underline"
        :class="{ 'is-active': editor?.isActive('underline') }"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <v-icon>mdi-format-underline</v-icon>
      </button>
      <button
        id="content-editor-tip-tap-code-btn"
        type="button"
        class="menubar__button"
        title="Code"
        :class="{ 'is-active': editor?.isActive('code') }"
        @click="editor.chain().focus().toggleCode().run()"
      >
        <v-icon>mdi-code-tags</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--blockquote-btn"
        type="button"
        class="menubar__button"
        title="Blockquote"
        :class="{ 'is-active': editor?.isActive('blockquote') }"
        @click="editor.chain().focus().toggleBlockquote().run()"
      >
        <v-icon>mdi-format-quote-close</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--paragraph-btn"
        type="button"
        class="menubar__button"
        :class="{ 'is-active': editor?.isActive('paragraph') }"
        title="Paragraph"
        @click="editor.chain().focus().setParagraph().run()"
      >
        <v-icon>mdi-format-paragraph</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--heading-1-btn"
        type="button"
        class="menubar__button"
        title="Heading 1"
        :class="{ 'is-active': editor?.isActive('heading', { level: 1 }) }"
        @click="editor.chain().focus().setHeading({ level: 1 }).run()"
      >
        <v-icon>mdi-format-header-1</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--heading-2-btn"
        type="button"
        class="menubar__button"
        title="Heading 2"
        :class="{ 'is-active': editor?.isActive('heading', { level: 2 }) }"
        @click="editor.chain().focus().setHeading({ level: 2 }).run()"
      >
        <v-icon>mdi-format-header-2</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--heading-3-btn"
        type="button"
        class="menubar__button"
        title="Heading 3"
        :class="{ 'is-active': editor?.isActive('heading', { level: 3 }) }"
        @click="editor.chain().focus().setHeading({ level: 3 }).run()"
      >
        <v-icon>mdi-format-header-3</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--heading-4-btn"
        type="button"
        class="menubar__button"
        title="Heading 4"
        :class="{ 'is-active': editor?.isActive('heading', { level: 4 }) }"
        @click="editor.chain().focus().setHeading({ level: 4 }).run()"
      >
        <v-icon>mdi-format-header-4</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--heading-5-btn"
        type="button"
        class="menubar__button"
        title="Heading 5"
        :class="{ 'is-active': editor?.isActive('heading', { level: 5 }) }"
        @click="editor.chain().focus().setHeading({ level: 5 }).run()"
      >
        <v-icon>mdi-format-header-5</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--bullets-btn"
        type="button"
        class="menubar__button"
        title="Bulleted List"
        :class="{ 'is-active': editor?.isActive('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <v-icon>mdi-format-list-bulleted</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--numbering-btn"
        type="button"
        class="menubar__button"
        title="Numbered List"
        :class="{ 'is-active': editor?.isActive('orderedList') }"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <v-icon>mdi-format-list-numbered</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--link-btn"
        type="button"
        class="menubar__button"
        :title="editor?.isActive('link') ? 'Update Link' : 'Add Link'"
        :class="{ 'is-active': editor?.isActive('link') }"
        @click="showLinkEditorDialog()"
      >
        <v-icon>mdi-link</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--unlink-btn"
        type="button"
        class="menubar__button"
        title="Remove Link"
        v-if="editor?.isActive('link')"
        @click="removeLinkMarkFromSelection()"
      >
        <v-icon>mdi-link-off</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--create-table-btn"
        type="button"
        class="menubar__button"
        title="Create Table"
        :class="{ 'is-active': editor?.isActive('table') }"
        @click="
          editor
            .chain()
            .focus()
            .insertTable({ rowsCount: 3, colsCount: 3, withHeaderRow: true })
            .run()
        "
      >
        <v-icon>mdi-table-large</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--add-table-btn"
        type="button"
        class="menubar__button"
        title="Add Table"
        v-if="editor?.isActive('table')"
        @click="
          editor
            .chain()
            .focus()
            .insertTable({ rowsCount: 3, colsCount: 3, withHeaderRow: true })
            .run()
        "
      >
        <v-icon>mdi-table-large-plus</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--delete-table-btn"
        type="button"
        class="menubar__button"
        title="Delete Table"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().deleteTable().run()"
      >
        <v-icon>mdi-table-large-remove</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--add-row-before-btn"
        type="button"
        class="menubar__button"
        title="Add Row Before"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().addRowBefore().run()"
      >
        <v-icon>mdi-table-row-plus-before</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--add-row-btn"
        type="button"
        class="menubar__button"
        title="Add Row After"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().addRowAfter().run()"
      >
        <v-icon>mdi-table-row-plus-after</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--delete-row-btn"
        type="button"
        class="menubar__button"
        title="Delete Row"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().deleteRow().run()"
      >
        <v-icon>mdi-table-row-remove</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--add-column-before-btn"
        type="button"
        class="menubar__button"
        title="Add Column Before"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().addColumnBefore().run()"
      >
        <v-icon>mdi-table-column-plus-before</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--add-column-after-btn"
        type="button"
        class="menubar__button"
        title="Add Column After"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().addColumnAfter().run()"
      >
        <v-icon>mdi-table-column-plus-after</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--delete-column-btn"
        type="button"
        class="menubar__button"
        title="Delete Column"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().deleteColumn().run()"
      >
        <v-icon>mdi-table-column-remove</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--toggle-header-row-btn"
        type="button"
        class="menubar__button"
        title="Toggle Header Row"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().toggleHeaderRow().run()"
      >
        <v-icon>mdi-table-row</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--toggle-header-column-btn"
        type="button"
        class="menubar__button"
        title="Toggle Header Column"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().toggleHeaderColumn().run()"
      >
        <v-icon>mdi-table-column</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--merge-cells-btn"
        type="button"
        class="menubar__button"
        title="Merge Cells"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().mergeCells().run()"
      >
        <v-icon>mdi-table-merge-cells</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--split-cell-btn"
        type="button"
        class="menubar__button"
        title="Split Cell"
        v-if="editor?.isActive('table')"
        @click="editor.chain().focus().splitCell().run()"
      >
        <v-icon>mdi-table-split-cell</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--add-image-btn"
        type="button"
        class="menubar__button"
        title="Add Image"
        v-if="supportImage"
        @click="addImage"
      >
        <v-icon>mdi-image</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--context-key-btn"
        type="button"
        class="menubar__button"
        title="context keys"
        v-if="supportContextKey"
        @click="showContextKeys"
      >
        <v-icon>mdi-clipboard-account</v-icon>
      </button>
      <button
        id="content-editor-tip-tap--add-placeholder-btn"
        type="button"
        class="menubar__button"
        @click="insertPlaceholderText"
        v-tooltip:bottom="`Insert Placeholder Text`"
        v-if="usePlaceholder !== null"
      >
        <v-icon>mdi-clipboard-text-outline</v-icon>
      </button>
    </div>
    <editor-content class="editor__content" :editor="editor"></editor-content>
    <tip-tap-image-upload-dialog
      ref="uploadDialog"
      :show-dialog="showImageUploadDialog"
      :token="requestToken"
      @closed="showImageUploadDialog = false"
      @onUpload="onImageUpload"
    ></tip-tap-image-upload-dialog>
    <tip-tap-context-keys-dialog
      ref="contextKeyDialog"
      :show-dialog="contextKeyDialog"
      @closed="contextKeyDialog = false"
      @insertContextKey="insertContextKey"
    >
    </tip-tap-context-keys-dialog>
    <tip-tap-link-editor-dialog
      :show-dialog="tipTapLinkEditorIsOpen"
      @hideLinkEditorDialog="hideLinkEditorDialog"
      :editor="editor"
      @removeLinkMarkFromSelection="removeLinkMarkFromSelection"
    ></tip-tap-link-editor-dialog>
  </div>
</template>

<script>
import * as tiptap from '@tiptap/vue-3';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Strike } from '@tiptap/extension-strike';
import { Underline } from '@tiptap/extension-underline';
import { Code } from '@tiptap/extension-code';
import { Blockquote } from '@tiptap/extension-blockquote';
import { Heading } from '@tiptap/extension-heading';
import { BulletList } from '@tiptap/extension-bullet-list';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { ListItem } from '@tiptap/extension-list-item';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import { History } from '@tiptap/extension-history';
import { Image } from '@tiptap/extension-image';
import { HardBreak } from '@tiptap/extension-hard-break';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Document from '@tiptap/extension-document';
import { unescape } from 'html-escaper';
import { mapGetters, mapState } from 'vuex';
import { CSBase } from '@complispace/cs-design-system';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Link from '@tiptap/extension-link';
import { MESSAGES, ATTRIBUTES } from '@/constants';
import { content } from '@/api';
import * as types from '@/store/mutationTypes';
import {
  collectingImagesFromContent,
  collectingDataHashFromImage,
  collectingDataSrcFromImage
} from '@/lib/strings';
import updateTableColumnsWidth from '@/lib/tableUtils';
import TipTapContextKeysDialog from './TipTapContextKeysDialog';
import TipTapImageUploadDialog from './TipTapImageUploadDialog';
import TipTapLinkEditorDialog from './TipTapLinkEditorDialog';

const { Editor, EditorContent, FloatingMenu, BubbleMenu } = tiptap; // for test to work

export default {
  name: 'CEContentEditorTipTap',

  props: {
    modelValue: {
      type: String,
      default: ''
    },

    showDialog: {
      type: Boolean,
      default: false
    },

    showSaveWorkMessage: {
      type: Boolean,
      required: false
    },

    supportImage: {
      types: Boolean,
      required: false,
      default: false
    },

    supportContextKey: {
      types: Boolean,
      required: false,
      default: false
    },

    options: {
      type: Object,
      required: false,
      default: () => ({})
    },
    usePlaceholder: {
      type: Boolean,
      default: null
    },
    placeholderText: {
      type: String,
      required: true,
      default: ''
    }
  },

  extends: CSBase,

  components: {
    EditorContent,
    FloatingMenu,
    BubbleMenu,
    TipTapImageUploadDialog,
    TipTapContextKeysDialog,
    TipTapLinkEditorDialog
  },

  data() {
    return {
      contextKeyDialog: false,
      tipTapLinkEditorIsOpen: false,
      editor: null,
      MESSAGES,
      showImageUploadDialog: false,
      imageCommand: null,
      requestToken: '',
      imagesInfo: null,
      enableEditor: true
    };
  },

  emits: ['update:modelValue'],

  watch: {
    usePlaceholder(newValue) {
      if (this.editor) {
        this.editor.setOptions({ editable: !newValue });
        const modelValue = newValue ? this.placeholderText : this.modelValue;
        // If the modelValue contains images, process it and set it to the editor
        if (collectingImagesFromContent(modelValue)) {
          this.getImagesFromContent(modelValue);
        }
        this.editor.commands.setContent(modelValue);
      }
    },
    showDialog() {
      // Every time the dialog is open, write the DOM with the content from parent.
      if (this.showDialog && this.editor) {
        this.getImagesFromContent();
        this.editor.commands.setContent(
          this.placeholderText ? this.placeholderText : this.modelValue
        );
      }
    },

    isLoadingImages(value) {
      if (value) {
        this.editor.setOptions({ editable: false });
      } else {
        this.editor.setOptions({ editable: true });
      }
    }
  },

  mounted() {
    this.initEditor();
    this.getImagesFromContent();
    this.applyStyledForErrorText();
  },

  beforeUnmount() {
    this.editor?.destroy();
  },

  computed: {
    ...mapGetters({
      getImageFromImageData: 'imageUploader/getImageFromImageData',
      getUploadedImages: 'imageUploader/getUploadedImages'
    }),

    ...mapState({
      isLoadingImages: (state) => state.appearance.isLoadingImages
    })
  },

  methods: {
    setImagesSrc(images, modelValue = this.modelValue, insertPlaceholderText = false) {
      const parser = new DOMParser();
      const parsedHtml = parser.parseFromString(modelValue, 'text/html');
      const imageElements = parsedHtml.getElementsByTagName('img');

      Array.prototype.forEach.call(imageElements, (imageElement, index) => {
        const dataHash = imageElement.getAttribute(ATTRIBUTES.DATA_FILENAME_HASH);

        const dataSRC = images.find((imageSrc) => imageSrc.hash === dataHash);

        if (dataSRC) {
          imageElements[index].alt = dataHash;
          imageElements[index].src = dataSRC.data;
        }
      });
      // only emit event when context key value is provided by user upon saving
      if (modelValue === this.modelValue) {
        this.$emit('update:modelValue', this.updateHTML(parsedHtml.body.innerHTML));
      }
      if (insertPlaceholderText) {
        this.editor.chain().focus().insertContent(parsedHtml.body.innerHTML).run();
      } else {
        this.editor.commands.setContent(this.updateHTML(parsedHtml.body.innerHTML));
      }
    },

    async getImagesFromContent(modelValue = this.modelValue, insertPlaceholderText = false) {
      const imagesInValue = collectingImagesFromContent(modelValue);
      const imagePromises = [];

      if (imagesInValue && imagesInValue.length) {
        imagesInValue.forEach((image) => {
          const dataHash = collectingDataHashFromImage(image);
          const src = collectingDataSrcFromImage(image);

          if (!src) {
            imagePromises.push(content.getImage(dataHash));
          }
        });
      }

      if (imagePromises.length) {
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, true);

        const responses = await Promise.all(
          imagePromises.map((promise) => promise.catch((err) => err))
        );

        const images = responses.map((response) => {
          const hash = response.config.url.split('/images/')[1];
          return {
            data: response.data,
            hash
          };
        });
        this.setImagesSrc(images, modelValue, insertPlaceholderText);
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, false);
      }
    },

    stripStartingAndEndingPTag(htmlString) {
      return htmlString.replace(/<\/?p>|<\/?table>/g, '');
    },

    async handlePasteContent(pasteContent) {
      const imagesInValue = collectingImagesFromContent(pasteContent);
      const imagePromises = [];
      const parser = new DOMParser();

      this.$nextTick(() => {
        const parsedHtml = parser.parseFromString(this.editor.getHTML(), 'text/html');

        const regexFixHtml = this.updateHTML(parsedHtml.body.innerHTML);

        const updateTableColmunsWidth = updateTableColumnsWidth(regexFixHtml);

        this.editor.commands.setContent(this.updateHTML(updateTableColmunsWidth));
      });

      if (imagesInValue && imagesInValue.length) {
        imagesInValue.forEach((image) => {
          const dataHash = collectingDataHashFromImage(image);
          const src = collectingDataSrcFromImage(image);

          if (!src) {
            imagePromises.push(content.getImage(dataHash));
          }
        });
      }

      if (imagePromises.length) {
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, true);

        const responses = await Promise.all(
          imagePromises.map((promise) => promise.catch((err) => err))
        );

        const images = responses.map((response) => {
          const hash = response.config.url.split('/images/')[1];
          return {
            data: response.data,
            hash
          };
        });
        this.setImagesSrc(images);
        this.$store.commit(types.APPEARANCE_SET_IS_LOADING_IMAGES, false);
      }
    },

    isEncodedUri(uri) {
      // eslint-disable-next-line prefer-regex-literals
      const uriRegex = new RegExp(/%[0-9a-f]{2}/i);
      return uri ? uriRegex.test(uri) : true;
    },

    updateHTML(html) {
      // Update links in HTML.
      const regex = /href\s*=\s*(['"])(https?:\/\/.+?)\1/gi;
      let unescapedHtml = unescape(html);
      const links = [...unescapedHtml.matchAll(regex)];

      links.forEach((link) => {
        const encodedUri = this.isEncodedUri(link[2]) ? link[2] : encodeURI(link[2]);
        const uri = encodedUri.replace(/\(/g, '%28').replace(/\)/g, '%29');
        unescapedHtml = unescapedHtml.replace(link[2], uri);
      });

      // Update line breaks in HTML.
      unescapedHtml = unescapedHtml.replace(/<p><\/p>/g, '<p><br></p>');

      return unescapedHtml;
    },

    getElementWidths(dom) {
      const result = [];

      const getElement = (element) => {
        if (element.className.includes('tableWrapper')) {
          const elementWidths = {
            // If all columns of the table have been resized, the table width can be obtained from the style.
            tableWidth: element.style.width,
            // If all columns of the table have not been resized, the table width is the same as the wrapper width.
            wrapperWidth: element.clientWidth
          };
          result.push(elementWidths);
        }

        if (element.children && element.children.length > 0) {
          Array.prototype.forEach.call(element.children, (child) => {
            getElement(child);
          });
        }
      };

      getElement(dom);
      return result;
    },

    initEditor() {
      const editorOptions = {
        content: this.modelValue,
        editorProps: {
          clipboardTextParser: (text) => {
            this.handlePasteContent(text);
          }
        },
        autoFocus: false,
        extensions: [
          Document,
          Paragraph,
          Text,
          Bold,
          Italic,
          Strike,
          Underline,
          Code,
          Blockquote,
          Heading.configure({
            levels: [1, 2, 3, 4, 5]
          }),
          BulletList,
          OrderedList,
          ListItem,
          Link.extend({ inclusive: false }).configure({
            protocols: ['http', 'https'],
            openOnClick: false,
            target: '__self',
            autolink: true
          }),
          Table.configure({ resizable: true }),
          TableCell,
          TableHeader,
          TableRow,
          History,
          Image.configure({ inline: true, allowBase64: true }),
          HardBreak,
          TextStyle,
          Color.configure({
            types: ['textStyle']
          })
        ]
      };

      const options = { ...editorOptions, ...this.options };
      // Create an Editor instance with above configuration options.
      this.editor = new Editor(options);

      this.editor.on('update', ({ editor }) => {
        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(editor.getHTML(), 'text/html');
        const tableElements = parsedHtml.getElementsByTagName('table');

        // Fix for table column resizing - Setting data attributes for table width.
        if (tableElements.length) {
          const domTableWidths = this.getElementWidths(this.editor.view.dom);

          Array.prototype.forEach.call(tableElements, (table, index) => {
            if (domTableWidths[index]) {
              const dataTableWidth = parsedHtml.createAttribute('data-tablewidth');
              const tableWidth =
                domTableWidths[index].tableWidth &&
                typeof domTableWidths[index].tableWidth === 'string'
                  ? domTableWidths[index].tableWidth.replace('px', '')
                  : domTableWidths[index].tableWidth;

              dataTableWidth.value = tableWidth;

              const dataTableWrapperWidth = parsedHtml.createAttribute('data-wrapperwidth');
              const wrapperWidth =
                domTableWidths[index].wrapperWidth &&
                typeof domTableWidths[index].wrapperWidth === 'string'
                  ? domTableWidths[index].wrapperWidth.replace('px', '')
                  : domTableWidths[index].wrapperWidth;

              dataTableWrapperWidth.value = wrapperWidth;

              // Setting data-tablewidth and data-wrapperwidth attributes for table elements.
              table.attributes.setNamedItem(dataTableWidth);
              table.attributes.setNamedItem(dataTableWrapperWidth);
            }
          });
          this.$emit('update:modelValue', this.updateHTML(parsedHtml.body.innerHTML));
        } else {
          this.$emit('update:modelValue', this.updateHTML(this.editor.getHTML()));
        }
      });
    },

    async addImage() {
      this.requestToken = await this.$store.dispatch('cs-session/getToken');

      this.showImageUploadDialog = true;
    },

    showContextKeys() {
      this.contextKeyDialog = true;
    },

    insertContextKey(contextKey) {
      const transaction = this.editor.state.tr.insertText(contextKey);
      this.editor.view.dispatch(transaction);

      this.contextKeyDialog = false;
    },

    async onImageUpload() {
      const images = this.getUploadedImages();

      const imagesContent = images
        .filter((image) => image.file)
        .map((image) => {
          const title = image.file.name.split('.')[0];
          const imageData = {
            type: 'image',
            attrs: {
              src: image.file.dataURL,
              alt: image.hashCode,
              title
            }
          };
          return imageData;
        });

      if (!imagesContent.length) return;

      this.editor?.chain().focus().insertContent(imagesContent).run();
    },

    showLinkEditorDialog() {
      const numberOfLinksHighlighted = this.checkNumberOfLinksHighlighted();
      if (numberOfLinksHighlighted > 1) {
        // eslint-disable-next-line no-alert
        alert(`You can't edit more than 1 link at a time.`);
        return;
      }
      this.tipTapLinkEditorIsOpen = true;
    },

    hideLinkEditorDialog() {
      this.tipTapLinkEditorIsOpen = false;
    },

    removeLinkMarkFromSelection() {
      if (!this.editor) return;
      this.editor.chain().focus().unsetLink().run();
    },

    checkNumberOfLinksHighlighted() {
      let numberOfLinksHighlighted = 0;

      if (!this.editor) return numberOfLinksHighlighted;

      const editorState = this.editor.state;
      const { from, to } = editorState.selection;
      editorState.doc.nodesBetween(from, to, (node) => {
        const eligibleMark = node.marks?.find((markItem) => markItem?.type?.name === 'link');
        if (eligibleMark) {
          numberOfLinksHighlighted += 1;
        }
      });
      return numberOfLinksHighlighted;
    },

    insertPlaceholderText() {
      if (this.placeholderText) {
        // If the placeholder text contains images, process placeholderText including images and insert it into the editor
        if (collectingImagesFromContent(this.placeholderText)) {
          this.getImagesFromContent(this.placeholderText, true);
        } else {
          // if the placeholder text does not contain images, insert it directly into the editor
          this.editor.chain().focus().insertContent(this.placeholderText).run();
        }
      } else {
        console.warn('No placeholder text available');
      }
    },

    async applyStyledForErrorText() {
      if (this.editor) {
        // to apply the style for error text
        this.editor.commands.setContent(this.modelValue);
      }
    }
  }
};
</script>

<style scoped>
.editor__content {
  word-wrap: break-word;
  overflow: auto;
  border: 2px solid #dddddd;
  padding-left: 1rem;
  padding-right: 1rem;
  flex-grow: 1;
  height: 100%;
}
.v-dialog .editor__content {
  height: 20rem !important;
}
.editor__content :deep(.tiptap.ProseMirror) {
  min-height: calc(100% - 30px);
}
.editor__content :deep(.ProseMirror:focus) {
  outline: none !important;
}

.editor__content :deep(h1),
.editor__content :deep(h2),
.editor__content :deep(h3),
.editor__content :deep(h4),
.editor__content :deep(h5),
.editor__content :deep(p),
.editor__content :deep(ul),
.editor__content :deep(ol),
.editor__content :deep(pre),
.editor__content :deep(blockquote) {
  margin: 1rem 0;
}

.editor__content :deep(h1),
.editor__content :deep(h2),
.editor__content :deep(h3),
.editor__content :deep(h4),
.editor__content :deep(h5) {
  line-height: 1.3;
}

.editor__content :deep(pre) {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: black;
  color: white;
  font-size: 0.8rem;
  overflow-x: auto;
}

.editor__content :deep(pre code) {
  display: block;
}

.editor__content :deep(p code) {
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
}

.editor__content :deep(ul),
.editor__content :deep(ol) {
  padding-left: 1rem;
}

.editor__content :deep(li > p),
.editor__content :deep(li > ol),
.editor__content :deep(li > ul) {
  margin: 0;
}

.editor__content :deep(ol ol) {
  list-style-type: lower-alpha;
}

.editor__content :deep(ol ol ol) {
  list-style-type: lower-roman;
}

.editor__content :deep(a) {
  color: rgb(0, 173, 239);
  cursor: pointer;
}

.editor__content :deep(blockquote) {
  border-left: 3px solid rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
  padding-left: 0.8rem;
  font-style: italic;
}

.editor__content :deep(blockquote p) {
  margin: 0;
}

.editor__content :deep(img) {
  display: block;
  margin: 0 auto 1rem auto;
  max-width: 80%;
  width: auto !important;
  height: auto !important;
  border-radius: 3px;
}

.editor__content :deep(table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}

.editor__content :deep(table th) {
  min-width: 1em;
  border: 1px solid #dddddd !important;
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.editor__content :deep(table td) {
  min-width: 1em;
  border: 1px solid #dddddd !important;
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.editor__content :deep(table td > *),
.editor__content :deep(table th > *) {
  margin-bottom: 0;
}

.editor__content :deep(table th) {
  font-weight: bold;
  text-align: left;
}

.editor__content :deep(table .selectedCell:after) {
  z-index: 2;
  position: absolute;
  content: '';
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgb(0, 173, 239, 0.2);
  pointer-events: none;
}

.editor__content :deep(table .column-resize-handle) {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  z-index: 20;
  background-color: rgb(0, 173, 239, 0.4);
  pointer-events: none;
}

.editor__content :deep(.tableWrapper) {
  margin: 1em 0;
  overflow-x: auto;
}

.editor__content :deep(.resize-cursor) {
  cursor: ew-resize;
  cursor: col-resize;
}

.menubar {
  display: flex;
  flex-wrap: wrap;
  transition: visibility 0.2s 0.4s, opacity 0.2s 0.4s;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.menubar.is-hidden {
  visibility: hidden;
  opacity: 0;
}

.menubar.is-focused {
  visibility: visible;
  opacity: 1;
  transition: visibility 0.2s, opacity 0.2s;
}

.menubar__button {
  font-weight: bold;
  display: inline-flex;
  background: transparent;
  border: 0;
  color: rgba(0, 0, 0, 0.54);
  padding: 0.2rem 0.5rem;
  margin-right: 0.2rem;
  border-radius: 3px;
  cursor: pointer;
}

.menubar__button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.menubar__button.is-active {
  background-color: rgba(0, 0, 0, 0.1);
}

.menubar__button:focus,
.menububble__input:focus {
  outline: none !important;
}

.menububble {
  display: flex;
  background: rgb(var(--v-theme-black-lighten-5));
  border-radius: 0.3rem;
  padding: 0;
  margin: 0;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s, visibility 0.2s;
}

.menububble.is-active {
  display: block;
  opacity: 1;
  visibility: visible;
  margin: 0;
}

.menububble__button {
  display: inline-flex;
  background: transparent;
  border: 0;
  color: #fff;
  padding: 0.2rem 0.5rem;
  margin-right: 0.2rem;
  border-radius: 3px;
  cursor: pointer;
}

.menububble__button.is-active {
  background-color: hsla(0, 0%, 100%, 0.2);
}

.menububble__button:last-child {
  margin-right: 0;
}

.menububble__form {
  display: flex;
}

.menububble__input {
  font: inherit;
  border: none;
  background: transparent;
  color: #fff;
  width: 100%;
  margin: 0 0.5rem 0 0.5rem;
}

#saveWorkInfoMessage {
  word-break: keep-all;
}

.editor__content :deep(br + br.ProseMirror-trailingBreak) {
  display: none;
  min-height: 100%;
}

.editor--disabled .editor__content :deep(*) {
  pointer-events: none;
}

.editor--disabled .editor__content {
  pointer-events: auto;
  overflow: auto;
}
</style>
